import { useCallback, useEffect, useState } from 'react';
import { EVENEMENT_MODE_CHANGE, fetchAdminAlerts, fetchAppMode } from '../../../services/adminApi';

// Etat permanent de l'en-tete et du menu de l'Admin : le MODE (pastille) et ce qui demande une ACTION (pastilles rouges).
// Releve toutes les minutes tant que l'onglet est visible (aucune requete en arriere-plan), et aussitot apres un changement
// de mode ou un retour sur l'onglet. Une panne de lecture n'efface rien : on garde la derniere valeur connue.

const PERIODE_MS = 60 * 1000;
// Dernière consultation du Trafic, gardée dans ce navigateur : les « nouvelles visites » sont celles arrivées depuis. La première fois,
// on part de maintenant (pas de pastille pour des visites déjà anciennes).
const CLE_TRAFIC_VU = 'bookipix.admin.trafficSeen';
const lireTraficVu = () => {
  try {
    const memorise = window.localStorage.getItem(CLE_TRAFIC_VU);
    if (memorise && !Number.isNaN(new Date(memorise).getTime())) return memorise;
    const maintenant = new Date().toISOString();
    window.localStorage.setItem(CLE_TRAFIC_VU, maintenant);
    return maintenant;
  } catch (_erreur) {
    return new Date().toISOString();
  }
};

export default function useAdminStatus(actif) {
  const [mode, setMode] = useState(null); // 'production' | 'test' | null (inconnu)
  const [alertes, setAlertes] = useState({ failedOrders: 0, incident: null, visits: null });

  const rafraichir = useCallback(async () => {
    const [modeResultat, alertesResultat] = await Promise.allSettled([fetchAppMode(), fetchAdminAlerts(lireTraficVu())]);
    if (modeResultat.status === 'fulfilled' && modeResultat.value?.mode) setMode(modeResultat.value.mode);
    if (alertesResultat.status === 'fulfilled' && alertesResultat.value) {
      setAlertes({
        failedOrders: Number(alertesResultat.value.failedOrders) || 0,
        incident: alertesResultat.value.incident || null,
        visits: alertesResultat.value.visits || null
      });
    }
  }, []);

  // Appelée à l'ouverture du Trafic : tout ce qui est arrivé jusque-là est « vu ».
  const marquerTraficVu = useCallback(() => {
    try { window.localStorage.setItem(CLE_TRAFIC_VU, new Date().toISOString()); } catch (_erreur) { /* sans mémoire locale, la pastille reste */ }
    setAlertes((precedent) => ({ ...precedent, visits: null }));
  }, []);

  useEffect(() => {
    if (!actif) return undefined;
    rafraichir();
    const minuteur = setInterval(() => { if (document.visibilityState !== 'hidden') rafraichir(); }, PERIODE_MS);
    const surRetour = () => { if (document.visibilityState === 'visible') rafraichir(); };
    document.addEventListener('visibilitychange', surRetour);
    window.addEventListener(EVENEMENT_MODE_CHANGE, rafraichir);
    return () => {
      clearInterval(minuteur);
      document.removeEventListener('visibilitychange', surRetour);
      window.removeEventListener(EVENEMENT_MODE_CHANGE, rafraichir);
    };
  }, [actif, rafraichir]);

  return { mode, alertes, rafraichir, marquerTraficVu };
}
